import { useEffect } from 'react'

/**
 * ConfirmModal — dark-themed replacement for `window.confirm()`.
 *
 * Goals:
 *   * Match the rest of the admin portal visually (dark surface +
 *     jarix-coral accents), so destructive actions feel native.
 *   * Support a two-stage flow: a primary warning message plus an
 *     optional detail block (for things like "12 registros históricos
 *     también se eliminarán").
 *   * Keyboard-friendly: Esc cancels, Enter confirms.
 *
 * Intentionally tiny (no trap focus library, no portal). A full modal
 * system is out of scope — this replaces the four or five native
 * confirm() calls across the admin module and stops there.
 */
interface ConfirmModalProps {
  open: boolean
  title: string
  message: string
  detail?: string | null
  /** Variant changes the icon + button color. Default is 'danger'. */
  variant?: 'danger' | 'warning' | 'info'
  confirmLabel?: string
  cancelLabel?: string
  /** When true, shows a spinner inside the confirm button. */
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export default function ConfirmModal({
  open,
  title,
  message,
  detail,
  variant = 'danger',
  confirmLabel,
  cancelLabel = 'Cancelar',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  // Keyboard shortcuts. Listener only mounts while the modal is open
  // so we don't interfere with other keyboard handlers when it's not.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (busy) return
      if (e.key === 'Escape') { e.preventDefault(); onCancel() }
      if (e.key === 'Enter') { e.preventDefault(); onConfirm() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, busy, onConfirm, onCancel])

  if (!open) return null

  const variantStyles = {
    danger: {
      iconBg: 'bg-red-500/20',
      iconColor: 'text-red-400',
      button: 'bg-red-500 hover:bg-red-600',
      path: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    },
    warning: {
      iconBg: 'bg-yellow-500/20',
      iconColor: 'text-yellow-400',
      button: 'bg-yellow-500 hover:bg-yellow-600',
      path: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    },
    info: {
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
      button: 'bg-blue-500 hover:bg-blue-600',
      path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
  }[variant]

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full ${variantStyles.iconBg} flex items-center justify-center flex-shrink-0`}>
            <svg className={`w-5 h-5 ${variantStyles.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={variantStyles.path} />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold">{title}</h3>
            <p className="text-gray-300 text-sm mt-1 leading-relaxed">{message}</p>
            {detail && (
              <div className="mt-3 p-3 rounded-lg bg-gray-900/60 border border-gray-700 text-xs text-gray-400">
                {detail}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-gray-700 px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-1.5 text-sm rounded font-semibold text-white ${variantStyles.button} disabled:opacity-50 flex items-center gap-2`}
          >
            {busy && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />}
            {confirmLabel || (variant === 'danger' ? 'Eliminar' : 'Confirmar')}
          </button>
        </div>
      </div>
    </div>
  )
}
